import { HttpParams } from "@angular/common/http";
import { forwardRef, Inject, Injectable } from "@angular/core";
import * as moment from 'moment';
import * as moment_tz from 'moment-timezone';
import { firstValueFrom } from "rxjs";
import { HISTORICAL_WORK_SESSION_DATA, HISTORICAL_WORK_SESSION_EXPORT_REPORTS, USER_HISTORICAL_WORK_SESSIONS_EXPORT_V2, USER_HISTORICAL_WORK_SESSIONS_V2 } from "../../../common/endpoints";
import { Location, PagedList, WorkSession } from "../../../model";
import { AlgorithmService } from "../../../service/algorithm.service";
import { AuthenticationService } from "../../../service/authentication.service";
import { ContextService } from "../../../service/context.service";
import { CustomPropertyService } from "../../../service/custom-property.service";
import { DownloadingObject, DownloadService } from "../../../service/download.service";
import { HttpService } from "../../../service/http.service";
import { AbstractContextService } from "../../../shared/class/abstract-context-service.class";
import { AbstractThingContextService } from "../../../shared/class/abstract-thing-context-service.class";
import { PropertyComponent } from "../../../shared/component";
import { CustomTableColumn, CustomTableService } from "../../../shared/custom-table";
import { DownloadStatus, DownloadType } from "../../../shared/download-dialog/download-dialog.component";
import { AbstractListWidgetV2Service } from "../../../shared/list-widget-v2/abstract-list-widget-v2.service";
import { HttpUtility } from "../../../utility";
import { ThingListWidgetV2Service } from "../../thing-list/thing-list-widget-v2.service";
import { AbstractWorkSessionListWidgetV2Service } from "../abstract-work-session-list-widget-v2.service";

@Injectable()
export class HistoricalWorkSessionListWidgetV2Service extends AbstractWorkSessionListWidgetV2Service<WorkSession> {

    private sortEnabled: boolean;

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => CustomPropertyService)) customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => AbstractContextService)) contextService: AbstractContextService,
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => AbstractThingContextService)) thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => ThingListWidgetV2Service)) thingListWidgetV2Service: ThingListWidgetV2Service,
        @Inject(forwardRef(() => DownloadService)) private downloadService: DownloadService,
        @Inject(forwardRef(() => ContextService)) protected timezoneContextService: ContextService,
        @Inject(forwardRef(() => HttpUtility)) private httpUtility: HttpUtility,
        @Inject(forwardRef(() => AlgorithmService)) algorithmService: AlgorithmService
    ) {
        super(authenticationService, customPropertyService, contextService, thingContextService, thingListWidgetV2Service, timezoneContextService, algorithmService);
    }

    getPagedList(page: number, size: number, sort: string[], metricNames: Set<string>, searchFields: string[], advancedSearchBody: any, location: Location, includeSubThingsAlerts: boolean): Promise<PagedList<WorkSession>> {
        let params = this.getParams(searchFields, advancedSearchBody, false, includeSubThingsAlerts);
        params = params.set('page', page + '');
        params = params.set('size', size + '');
        this.setSortEnabled(advancedSearchBody?.startTimestamp, advancedSearchBody?.endTimestamp);
        if (this.sortEnabled && sort && sort[0]) {
            if (sort[0].startsWith(AbstractListWidgetV2Service.COMPOSITE_SORT_PREFIX)) {
                sort = this.assignCompositeSort(sort);
            }
            params = params.set('sort', sort.join(','));
        }
        return firstValueFrom(this.httpService.get<PagedList<WorkSession>>(USER_HISTORICAL_WORK_SESSIONS_V2, params));
    }

    private setSortEnabled(startTimestamp: number, endTimestamp: number): void {
        if (startTimestamp && endTimestamp) {
            let duration = moment.duration(endTimestamp - startTimestamp);
            let days = duration.asDays();
            const maxDays = this.authenticationService.getTenant().workSessionMaxSortablePeriodDays;
            this.sortEnabled = (!!this.thingContextService.getCurrentThing() || !!this.contextService.getCurrentLocation()) && maxDays > 0 && days <= maxDays;
        } else {
            this.sortEnabled = false;
        }
    }

    isSortEnabled(): boolean {
        return this.sortEnabled;
    }

    downloadCSV(params: HttpParams, fileName: string): void {
        const downloadingObject: DownloadingObject = {
            fileName: fileName || 'historicalWorkSessions.csv',
            uuid: null,
            status: DownloadStatus.DOWNLOADING,
            type: DownloadType.CSV,
            csvEndpoint: USER_HISTORICAL_WORK_SESSIONS_EXPORT_V2,
            params: params
        }
        this.downloadService.addDownloadingObject(downloadingObject);
        this.downloadService.setVisible();
    }

    protected getPropertyColumn(col: PropertyComponent, columnName: string, defaultType: string): CustomTableColumn {
        switch (col.name) {
            case 'customer.name':
                return CustomTableService.newCustomerLinkColumn(this.getLabel(col, defaultType), 'customer').withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'location.name':
                return CustomTableService.newLocationLinkColumn(this.getLabel(col, defaultType), 'location').withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.serviceLevel':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType)).withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'location.country':
            case 'location.timezone':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType))
                    .withAlternativePath(col.name.replace('location', 'customer')).withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.gpsPosition':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType))
                    .withAlternativePath('location.gpsPosition').withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.tags':
                return CustomTableService.newTagColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType)).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.parentThingId':
                return CustomTableService.newParentThingColumn(this.getLabel(col, defaultType), columnName).withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'date':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), 'startTimestamp', this.getFilter(col, defaultType)).withSortField(this.sortEnabled ? "startTimestamp" : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'duration':
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), 'durationRaw', this.getFilter(col, defaultType)).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.name':
                return CustomTableService.newThingLinkColumn('thingName', this.getLabel(col, defaultType), 'thing', 'name').withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'thing.serialNumber':
                return CustomTableService.newThingLinkColumn('thingSerialNumber', this.getLabel(col, defaultType), 'thing', 'serialNumber').withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'customer.partner.name':
            case 'location.partner.name':
            case 'thing.partner.name':
                return CustomTableService.newPartnerLinkColumn(this.getLabel(col, defaultType), col.name.substring(0, (col.name.length - ('name'.length) - 1))).withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
            case 'reportStorageKey':
                return CustomTableService.newButtonColumn('exportReport', '', 'id', 'float-right', 'Download report').withMatIcon('docs').withMatIconClass('material-symbols-outlined').withVisiblePath('reportStorageKey').withStyle({ '_any': { 'font-size': '20px', 'width': '10px' } }).withColumnClass('no-print')
            case 'dataImport.storageKey':
                return CustomTableService.newButtonColumn('downloadData', '', 'id', 'float-right', 'Download data').withMatIcon('download').withMatIconClass('material-symbols-outlined').withVisiblePath('showDownloadData').withStyle({ '_any': { 'font-size': '20px', 'width': '10px' } }).withColumnClass('no-print')
            default:
                return CustomTableService.newPipedColumn(columnName, this.getLabel(col, defaultType), col.name, this.getFilter(col, defaultType)).withArgument(this.getArgument(col))
                    .withValueMap(this.getValueMap(col)).withSortField(this.sortEnabled ? col.name : null).withColumnClass(col.columnClass).withShowHeader(col.showHeader);
        }
    }

    getDeleteIconMap() {
        return {
            "_default": {
                isFontAwesome: true,
                fontAwesomeIcon: ['fas', 'trash']
            }
        }
    }

    exportReports(body: any, params: HttpParams): Promise<any> {
        const now = moment_tz.tz(this.timezoneContextService.getContextTimezone()).format('YYYYMMDD_HHmm');
        const thing = this.thingContextService.getCurrentThing();
        return firstValueFrom(this.httpService.post<any>(HISTORICAL_WORK_SESSION_EXPORT_REPORTS, body, params)).then(response => {
            return firstValueFrom(this.httpService.externalGet(response.url, { responseType: 'blob' })).then(blob => {
                let fileName = response.filename ? response.filename : ((thing.serialNumber ? (thing.serialNumber + '_') : '') + now + '_reports.zip');
                this.httpUtility.wrapFileAndDownload({ file: blob, fileName: fileName });
            });
        });
    }

    downloadData(id: string): Promise<any> {
        return firstValueFrom(this.httpService.get<any>(HISTORICAL_WORK_SESSION_DATA.replace('{id}', id))).then(response => {
            return firstValueFrom(this.httpService.externalGet(response.url, { responseType: 'blob' })).then(blob => {
                let fileName = 'work_session_data.zip';
                this.httpUtility.wrapFileAndDownload({ file: blob, fileName: fileName });
            });
        });
    }

}